import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  type OnChanges,
  type OnInit,
  type SimpleChanges,
  inject,
} from '@angular/core';
import { IconModule } from '@freelancer/ui/icon';
import { IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkUnderline, QueryParams } from '@freelancer/ui/link';
import { ListItemComponent } from '@freelancer/ui/list-item';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { textVariants } from '@freelancer/ui/tailwind-text';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { LinkModule } from '../link/link.module';

@Component({
  standalone: true,
  selector: 'fl-split-button-option',
  imports: [IconModule, LinkModule, ListItemComponent, NgTemplateOutlet],
  template: `
    @if (!link) {
      <div [class]="splitButtonTextClasses">
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </div>
    }

    @if (link) {
      <fl-link
        [color]="LinkColor.FOREGROUND"
        [link]="link"
        [newTab]="newTab"
        [queryParams]="linkQueryParams"
        [size]="fontSize"
        [sizeTablet]="fontSizeTablet"
        [sizeDesktop]="fontSizeDesktop"
        [underline]="LinkUnderline.NEVER"
      >
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </fl-link>
    }
    <ng-template #content>
      <fl-list-item
        [clickable]="true"
        [bordered]="false"
        [padding]="optionPadding"
        [transparent]="true"
      >
        <div class="OptionListItem">
          @if (iconName) {
            <fl-icon
              class="mr-xxsmall"
              [name]="iconName"
              [size]="iconSize"
            ></fl-icon>
          }
          {{ displayText }}
        </div>
      </fl-list-item>
    </ng-template>
  `,
  styleUrls: ['./split-button-option.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplitButtonOptionComponent implements OnInit, OnChanges {
  changeDetectorRef = inject(ChangeDetectorRef);

  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;

  @Input() fontSize = TextSize.XSMALL;
  @Input() fontSizeTablet?: TextSize;
  @Input() fontSizeDesktop?: TextSize;
  @Input() iconSize = IconSize.MID;
  @Input() optionPadding = ListItemPadding.XSMALL;
  @Input() link?: string;
  @Input() linkQueryParams?: QueryParams;
  @Input() newTab?: boolean = false;
  @Input() iconName?: string;
  @Input() displayText: string;

  splitButtonTextClasses: string;

  @HostBinding('attr.role') ariaRole = 'listitem';

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'fontSize' in changes ||
      'fontSizeTablet' in changes ||
      'fontSizeDesktop' in changes
    ) {
      this.computeClasses();
    }
  }

  ngOnInit(): void {
    this.computeClasses();
  }

  /**
   * Dedicated API for parent component to apply styling configuration
   */
  applyStyles(config: {
    fontSize?: TextSize;
    fontSizeTablet?: TextSize;
    fontSizeDesktop?: TextSize;
    optionPadding?: ListItemPadding;
    iconSize?: IconSize;
  }): void {
    let hasChanges = false;

    if (config.fontSize !== undefined && this.fontSize !== config.fontSize) {
      this.fontSize = config.fontSize;
      hasChanges = true;
    }

    if (
      config.fontSizeTablet !== undefined &&
      this.fontSizeTablet !== config.fontSizeTablet
    ) {
      this.fontSizeTablet = config.fontSizeTablet;
      hasChanges = true;
    }

    if (
      config.fontSizeDesktop !== undefined &&
      this.fontSizeDesktop !== config.fontSizeDesktop
    ) {
      this.fontSizeDesktop = config.fontSizeDesktop;
      hasChanges = true;
    }

    // We only need to recompute classes if any font size changed
    if (hasChanges) {
      this.computeClasses();
    }

    if (
      config.optionPadding !== undefined &&
      this.optionPadding !== config.optionPadding
    ) {
      this.optionPadding = config.optionPadding;
      hasChanges = true;
    }

    if (config.iconSize !== undefined && this.iconSize !== config.iconSize) {
      this.iconSize = config.iconSize;
      hasChanges = true;
    }

    if (hasChanges) {
      this.changeDetectorRef.markForCheck();
    }
  }

  private computeClasses(): void {
    this.splitButtonTextClasses = textVariants({
      size: this.fontSize,
      sizeTablet: this.fontSizeTablet,
      sizeDesktop: this.fontSizeDesktop,
      color: FontColor.FOREGROUND,
      weight: FontWeight.NORMAL,
    });
  }
}
